import { gql, useQuery } from '@apollo/client';
import { Input, InputGroup, InputLeftElement, Stack, Text } from '@chakra-ui/react';
import * as React from 'react';

import { EEventType, SortOptions } from '@/graphql-types/globalTypes';
import { Card } from '@/imports/ui/chakra/components/Card';
import { UnexpectedError } from '@/imports/ui/chakra/components/UnexpectedError';
import { EventList } from '@/imports/ui/components/EventList';
import { trackClickEvent } from '@/imports/ui/pages/events/EventsPage/components/EventsPageMain/components/analytics/track-click-event';
import { SearchIcon } from '@/imports/ui/chakra/feather';
import { useDebounce } from '@/imports/ui/hooks/useDebounce';

const TENANT_NAME = gql`
  query LearningCoursesPage_Tenant {
    tenant {
      result {
        displayName
      }
    }
  }
`;

const LEARNING_COURSES = gql`
  query LearningCoursesPage_Courses($input: EventsInput!) {
    events(input: $input) {
      results {
        _id
        isCourseWorkshop
        intutoCourseCollection {
          collectionId
          completeInSequence
        }
        ...EventList_EventFragment
        ...TrackClickEvent_EventFragment
      }
      hasMore
    }
  }
  ${EventList.eventFragment}
  ${trackClickEvent.eventFragment}
`;

export const LearningCoursesPage: React.FC = () => {
  const [searchDraft, setSearchDraft] = React.useState('');
  const [searchQuery, setSearchQuery] = React.useState('');

  const applySearch = useDebounce((value: string) => {
    setSearchQuery(value.trim());
  }, 400);

  const learningInput = React.useMemo(
    () => ({
      limit: 15,
      offset: 0,
      filter: {
        eventType: EEventType.CourseWorkshop,
        ...(searchQuery ? { searchQuery } : {}),
      },
      sort: {
        startDate: SortOptions.asc,
      },
    }),
    [searchQuery]
  );

  const tenantQuery = useQuery(TENANT_NAME);
  const coursesQuery = useQuery(LEARNING_COURSES, {
    notifyOnNetworkStatusChange: true,
    variables: {
      input: learningInput,
    },
    fetchPolicy: 'cache-and-network',
  });

  const { fetchMore, refetch } = coursesQuery;
  const rawResults =
    coursesQuery.data?.events.results || coursesQuery.previousData?.events.results;
  const visibleResults = React.useMemo(() => {
    return (rawResults || []).filter((event: any) => {
      if (!event?.isCourseWorkshop) return true;
      return !event?.intutoCourseCollection?.completeInSequence;
    });
  }, [rawResults]);

  React.useEffect(() => {
    const handleRsvpUpdate = () => {
      void refetch();
    };
    if (typeof window !== 'undefined') {
      window.addEventListener('rsvp-updated', handleRsvpUpdate);
    }
    return () => {
      if (typeof window !== 'undefined') {
        window.removeEventListener('rsvp-updated', handleRsvpUpdate);
      }
    };
  }, [refetch]);

  const tenantName =
    tenantQuery.data?.tenant.result?.displayName ?? 'Your organization';

  if (coursesQuery.error || tenantQuery.error) {
    return <UnexpectedError />;
  }

  const currentLength = rawResults?.length || 0;

  return (
    <Stack spacing="4" width="100%">
      <Stack spacing="1">
        <Text textStyle="xlBodyStrong">Courses</Text>
        <Text textStyle="detail" color="gray.600">
          Learning from {tenantName}.
        </Text>
      </Stack>

      <Card>
        <Stack spacing="4" p={{ base: 3, md: 4 }}>
          <InputGroup>
            <InputLeftElement pointerEvents="none">
              <SearchIcon color="gray.600" />
            </InputLeftElement>
            <Input
              variant="filled"
              borderRadius="full"
              placeholder="Search courses"
              value={searchDraft}
              onChange={(e) => {
                const v = e.target.value;
                setSearchDraft(v);
                applySearch(v);
              }}
            />
          </InputGroup>
        </Stack>
      </Card>

      <EventList
        events={visibleResults as any}
        networkStatus={coursesQuery.networkStatus}
        hasMore={coursesQuery.data?.events.hasMore}
        fetchMore={() =>
          fetchMore({
            variables: {
              input: {
                ...learningInput,
                offset: currentLength,
              },
            },
            updateQuery: (prev, { fetchMoreResult }) => {
              if (!fetchMoreResult) return prev;
              const combined = [
                ...prev.events.results,
                ...fetchMoreResult.events.results,
              ];
              const seen = new Set<string>();
              const deduped = combined.filter((ev) => {
                const id = ev._id as string;
                if (seen.has(id)) return false;
                seen.add(id);
                return true;
              });
              return {
                ...prev,
                events: {
                  __typename: prev.events.__typename,
                  hasMore: fetchMoreResult.events.hasMore,
                  results: deduped,
                },
              };
            },
          })
        }
        trackClickEvent={(eventId) => {
          const ev = coursesQuery.data?.events.results?.find(
            (row: { _id: string }) => row._id === eventId
          );
          ev && trackClickEvent(ev);
        }}
        empty={
          <Card>
            <Text p={{ base: 3, md: 4 }} textStyle="detail" color="gray.600">
              No courses are available yet.
            </Text>
          </Card>
        }
      />
    </Stack>
  );
};
